import { CheckCircle, ChevronRight, Circle, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { onboardingChecklistItems, tutorialStorageKeys } from '../tutorial/tutorialSteps';
import { cn } from '../utils/helpers';

export default function OnboardingChecklist({ barbershopId, status = {}, onRestartTutorial }) {
  const manualProgress = JSON.parse(localStorage.getItem(tutorialStorageKeys.checklist(barbershopId)) || '{}');
  const items = onboardingChecklistItems.map(item => ({
    ...item,
    completed: item.match(status) || !!manualProgress[item.id]
  }));
  const completed = items.filter(item => item.completed).length;
  const progress = Math.round((completed / items.length) * 100);

  const markPublicPageVisited = () => {
    const next = { ...manualProgress, public_page: true };
    localStorage.setItem(tutorialStorageKeys.checklist(barbershopId), JSON.stringify(next));
  };

  return (
    <div className="card p-5 sm:p-6" data-tour="onboarding-checklist">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-primary-400/25 bg-primary-500/10">
            <Sparkles className="h-5 w-5 text-primary-300" />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-300">Primeiros passos</p>
            <h2 className="mt-1 text-lg font-bold text-white">Configure sua barbearia</h2>
            <p className="mt-1 text-sm text-gray-400">Complete o setup para deixar agenda, WhatsApp e vendas prontos.</p>
          </div>
        </div>
        <button onClick={onRestartTutorial} className="btn-secondary py-2 px-4 text-sm" data-tour="tutorial-button">
          Ver tutorial novamente
        </button>
      </div>

      <div className="mt-5">
        <div className="mb-3 flex items-center justify-between text-xs">
          <span className="font-semibold text-gray-300">{completed}/{items.length} concluido</span>
          <span className="text-primary-300">{progress}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-white/5">
          <div className="h-full rounded-full bg-gradient-to-r from-primary-500 to-blue-500 transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="mt-5 grid gap-2 md:grid-cols-2 xl:grid-cols-5">
        {items.map(item => {
          const content = (
            <div className={cn(
              'group flex h-full items-start gap-3 rounded-2xl border p-3 text-left transition',
              item.completed
                ? 'border-green-500/20 bg-green-500/10'
                : 'border-white/10 bg-white/5 hover:border-primary-400/40 hover:bg-primary-500/10'
            )}>
              {item.completed ? (
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-green-300" />
              ) : (
                <Circle className="mt-0.5 h-5 w-5 shrink-0 text-gray-500 group-hover:text-primary-300" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-white">{item.label}</p>
                <p className="mt-1 text-xs leading-5 text-gray-400">{item.description}</p>
              </div>
              {!item.completed && <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-gray-500 group-hover:text-primary-300" />}
            </div>
          );

          if (item.id === 'public_page' && status.publicUrl) {
            return (
              <a key={item.id} href={status.publicUrl} target="_blank" rel="noreferrer" onClick={markPublicPageVisited}>
                {content}
              </a>
            );
          }

          if (item.target) {
            return (
              <Link key={item.id} to={`/dashboard/${barbershopId}/${item.target}`}>
                {content}
              </Link>
            );
          }

          return <div key={item.id}>{content}</div>;
        })}
      </div>
    </div>
  );
}
